import { view } from "@risingstack/react-easy-state";
import { Breadcrumbs, Button, Grid, Group } from "@tine/designsystem-ui-react";
import find from "lodash-es/find";
import isNil from "lodash-es/isNil";
import isUndefined from "lodash-es/isUndefined";
import isEmpty from "lodash/isEmpty";
import { Helmet } from "react-helmet";
import { Link } from "wouter";

import type { Product } from "../../common/types/productTypes";
import { LIST_TYPE } from "../../common/types/trackingTypes";
import { stripPossibleHtmlExtension, titleCase } from "../../common/utils";

import categoryStore from "../../stores/categories/categoryStore";
import productStore from "../../stores/product/productStore";

import { IconPrint } from "@tine/designsystem-icons/sharp";
import type { BreadcrumbsProps } from "@tine/designsystem-ui-react/src/components/breadcrumbs/Breadcrumbs";
import { trimEnd } from "lodash-es";
import Loading from "../../components/Loading";
import NotFound from "../../components/NotFound";
import WhiteSheet from "../../components/WhiteSheet";
import ProductList from "../../components/common/product/ProductList";
import ExpiredProductDetails from "./ExpiredProductDetails";
import ProductDescriptionAndContent from "./ProductDescriptionAndContent";
import ProductDetailsImage from "./ProductDetailsImage";
import ProductDetailsInfo from "./ProductDetailsInfo";
import ProductDetailsPrint from "./ProductDetailsPrint";

type ProductDetailsPageProps = {
   slug: string;
};

export const AllergensDisplay = ({ product }: { product: Product | undefined }) => {
   if (isNil(product?.allergens) || isEmpty(product.allergens)) {
      return null;
   }

   const parts = product.allergens.split(". ");
   return parts.map((part) => <p key={part}>{trimEnd(part, ".")}.</p>);
};

const ProductDetailsPage = ({ slug }: ProductDetailsPageProps) => {
   const urlKey = stripPossibleHtmlExtension(slug);
   const product = productStore.findProductByUrlKey(urlKey);

   if (!productStore.productsLoaded) {
      return <Loading />;
   }

   if (isUndefined(product)) {
      return <NotFound />;
   }

   const catInfo = find(categoryStore.findCategoryKeysBySku(product.sku).map((key) => categoryStore.findCategoryByUrlKey(key)));

   const breadcrumbItems: BreadcrumbsProps["items"] =
      catInfo?.levels.map((c) => ({
         label: titleCase(c.name),
         href: c.fullUrl,
         as: Link
      })) || [];

   // Add product name to breadcrumb
   breadcrumbItems.length && breadcrumbItems.push({ label: product.name, href: "", as: Link });

   return (
      <>
         <div className="print:tw-hidden tw-relative">
            <Helmet>
               <title>{product.name}</title>
               {!!product.description && <meta name="description" content={product.description.toString()} />}
            </Helmet>

            <div className="tw-mb-4">
               <Breadcrumbs items={breadcrumbItems} />
            </div>

            {product.expired ? (
               <ExpiredProductDetails product={product} />
            ) : (
               <>
                  <WhiteSheet className="tw-py-12">
                     <Grid cols={12} gap={{ base: 2, lg: 8 }} className="tw-relative tw-pb-6">
                        <div className="tw-absolute tw-top-0 tw-right-0 tw-z-20 tw-hidden lg:tw-block">
                           <Button size="compact" onClick={() => window.print()} variant="tertiary" icon={<IconPrint />}>
                              Skriv ut produktdatablad
                           </Button>
                        </div>
                        <Grid.Col span={{ base: 12, lg: 4 }} className="tw-relative">
                           <ProductDetailsImage product={product} />
                        </Grid.Col>

                        <Grid.Col span={{ base: 12, lg: 8 }} className="tw-pb-6 tw-relative">
                           <ProductDetailsInfo product={product} />
                        </Grid.Col>
                     </Grid>
                     <ProductDescriptionAndContent product={product} />
                  </WhiteSheet>
                  <div className="lg:tw-hidden tw-py-5">
                     <Button size="compact" onClick={() => window.print()} variant="secondary" icon={<IconPrint />}>
                        Skriv ut produktdatablad
                     </Button>
                  </div>

                  <Group justify="start" className="tw-mt-6 tw-border-ink-border-subtle">
                     <ProductList
                        skus={product.similarProducts}
                        listName={"Andre anbefalte produkter"}
                        heading={"Andre anbefalte produkter"}
                        listType={LIST_TYPE.RECOMMENDATIONS}
                        context="recommendations"
                        count={6}
                     />
                  </Group>
               </>
            )}
         </div>
         <ProductDetailsPrint product={product} />
      </>
   );
};

export default view(ProductDetailsPage);
